import { PopoverOrigin } from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { brandAlpha, brandColors, hoverOnly } from "../../theme/mixins";

// The theme's MuiAppBar override already supplies the frosted-glass treatment
// (translucent white, blur, hairline bottom border). Only the square corners
// need restating, since MuiPaper rounds by default.
export const getAppBarStyles = (theme: Theme): SxProps<Theme> => {
    return {
        borderRadius: 0,
    };
};

/**
 * How much room the sticky app bar takes off the top of every page.
 *
 * Exported because anything else that sticks has to clear it, and a second copy of these
 * two numbers would drift the first time the toolbar changed height -- see the schedule's
 * day-name rail in `pages/MyCalendar/myCalendar.styles.ts`.
 */
export const APP_BAR_HEIGHT = { xs: 64, md: 72 } as const;

export const toolbarStyles: SxProps<Theme> = {
    justifyContent: 'space-between',
    minHeight: APP_BAR_HEIGHT,
};

// `minWidth: 0` and the shrink are what let the logo give way when the toolbar runs out
// of room, instead of pushing the controls off the side of the screen. It is the only
// thing here that can afford to: everything to its right is either a control or the way
// out of the page.
export const logoStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    minWidth: 0,
    flexShrink: 1,
    transition: 'opacity .2s ease',
    ...hoverOnly({ opacity: 0.8 }),
};

// Sized against toolbarStyles' minHeight (64/72). The source asset
// (motion-care-header-logo.png) is cropped tight to its artwork, with no
// transparent margin, so this height renders as visible logo, not padding.
//
// `maxHeight` rather than `height`, paired with `maxWidth`: a fixed height and an auto
// width cannot both be honoured when the toolbar is narrower than the artwork, and the
// browser resolves that by squashing the wordmark. Capping both and letting the image
// keep its own ratio means a narrow screen gets a smaller logo rather than a distorted
// one. Which screens: Romanian at 320px, where "Autentificare" is three times the width
// of "Login" and something has to give.
export const logoImageStyles: SxProps<Theme> = {
    maxHeight: { xs: 34, md: 38 },
    maxWidth: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
};

/**
 * The clinic's name, drawn where its logo would go.
 *
 * Sized to sit in the same place as `logoImageStyles`, so the header does not change height
 * depending on whether a clinic has uploaded a mark. `--ch-primary` rather than a fixed colour:
 * this IS the clinic's branding when there is no image to carry it.
 */
export const wordmarkStyles: SxProps<Theme> = {
    fontSize: { xs: '1.15rem', md: '1.35rem' },
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: 'var(--ch-primary)',
    // The image it stands in for is capped at this height; a line that tall keeps the toolbar
    // still and the name centred in it. A block rather than a flex row because only a block
    // can end in an ellipsis -- and on a 320px phone, with the bell beside the avatar, a long
    // clinic name has to end somewhere other than underneath the language picker.
    display: 'block',
    lineHeight: { xs: '34px', md: '38px' },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

/**
 * In the flow of the bar, between the logo and the controls, and centred in what is left.
 *
 * It used to be centred on the bar itself (`position: absolute`), which looks the same on a
 * wide screen and drew the last entries over the language picker on a narrow one: nothing
 * told the nav and the controls about each other, and a therapist's six entries met them at
 * anything under about 1300px. Sharing the row is what stops that -- a nav short of room
 * wraps its labels instead of running under the bell.
 */
export const buttonsContainerStyles: SxProps<Theme> = {
    display: { xs: 'none', md: 'flex' },
    flex: 1,
    justifyContent: 'center',
    gap: 1,
    minWidth: 0,
    mx: 1,
};

export const buttonStyles: SxProps<Theme> = {
    color: brandColors.foreground,
    fontWeight: 500,
    px: 2,
    ...hoverOnly({
        backgroundColor: brandAlpha('primary', 0.08),
        color: brandColors.primary,
    }),
};

// Never squeezed: these are the controls, and a half-width language picker is worse than
// a smaller logo. The logo is the one thing in the toolbar that gives way -- see logoStyles.
export const rightContainerStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: 1
};

export const languageSelectFormStyles: SxProps<Theme> = {
    minWidth: 50,
    border: 0,
    padding: 0.25,
};

export const languageSelectStyles: SxProps<Theme> = {
    color: brandColors.foreground,
    '& .MuiSelect-icon': {
        color: brandColors.foreground,
    },
    '& fieldset': {
        border: 'none',
    },
};

/**
 * Only where there is room to spare. A therapist's six entries in Romanian are some 600px
 * wide, and with the greeting and the bell beside the avatar that needs about 1300px of bar;
 * below `xl` the greeting is what gives way, before the nav has to wrap.
 */
export const welcomeTextStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: { xs: 'none', xl: 'block' },
    fontWeight: 500,
};

export const iconStyles: SxProps<Theme> = {
    mr: 1
};

export const transformOrigin: PopoverOrigin = {
    horizontal: 'right',
    vertical: 'top'
};

export const anchorOrigin: PopoverOrigin = {
    horizontal: 'right',
    vertical: 'bottom'
};

/*
 * The two ways in, for a visitor who has not signed in.
 *
 * Only one of them fits on a phone. The theme's MuiButton override pads every button
 * 24px each side, so in Romanian -- the primary market -- "Autentificare" and
 * "Înregistrare" come to 272px between them, against a 320px screen that also has to
 * hold a logo and a language picker. The pair used to be drawn anyway and the second one
 * fell off the side of the screen: at 375px the document was 484px wide and Register was
 * not merely clipped, it was entirely past the edge.
 *
 * Sign in is the one that stays. It is what a returning patient came for, and the sign-in
 * page carries its own "register here" link, so registering is one tap further away
 * rather than gone. Both are drawn from `sm` up, where there is room for them.
 */

/** Narrower padding on a phone, since this is the button that has to fit. */
export const getLoginButtonStyles = (theme: Theme): SxProps<Theme> => {
    return {
        color: theme.palette.primary.main,
        borderColor: brandAlpha('primary', 0.5),
        px: { xs: 1.5, sm: 3 },
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...hoverOnly({
            borderColor: theme.palette.primary.main,
            backgroundColor: brandAlpha('primary', 0.06),
        }),
    };
};

/**
 * Unused while the Register button is hidden from the header -- kept, not deleted,
 * because self-registration is coming back. See the comment where the button was, in
 * Header.tsx.
 */
export const getRegisterButtonStyles = (theme: Theme): SxProps<Theme> => {
    return {
        color: '#FFFFFF',
        backgroundColor: theme.palette.primary.main,
        // Not `visibility` or an opacity trick: `display: none` is what also takes it out
        // of the tab order and out of the accessibility tree, so a phone reader is not
        // offered a button nobody can see.
        display: { xs: 'none', sm: 'inline-flex' },
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...hoverOnly({ backgroundColor: theme.palette.primary.dark }),
    };
};

export const drawerPaperStyles: SxProps<Theme> = {
    width: 260,
    backgroundColor: 'background.paper',
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
};

export const drawerItemStyles: SxProps<Theme> = {
    borderRadius: 999,
    mx: 1,
    my: 0.25,
    ...hoverOnly({
        backgroundColor: brandAlpha('primary', 0.08),
        color: brandColors.primary,
    }),
};

/**
 * "Acting as" -- an administrator in one of their therapists' sessions. Fixed to the foot of
 * the screen, above the page and below any dialog, and inside the 16px gutter on a phone.
 */
export const impersonationBannerStyles: SxProps<Theme> = {
    position: 'fixed',
    // The bar's own layer: above the page's positioned content, which would otherwise paint
    // over it in document order, and below every dialog.
    zIndex: 'appBar',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'max-content',
    maxWidth: 'calc(100vw - 32px)',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    py: 1,
    px: 2,
    borderRadius: 999,
    bgcolor: brandColors.primary,
    color: brandColors.primaryContrast,
};

export const impersonationTextStyles: SxProps<Theme> = {
    fontWeight: 600,
    minWidth: 0,
};
